import React, { useEffect, useState } from "react";
import { api, fmtPKR, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  CheckCircle2, XCircle, Hourglass, FileImage, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function AdminPaymentVerification() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [preview, setPreview] = useState(null);
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/payment-submissions${q}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (sub) => {
    if (!window.confirm(`Approve payment of ${fmtPKR(sub.amount)} from ${sub.shop_name}? Plan will activate immediately and a commission row will be created.`)) return;
    try {
      await api.post(`/admin/payment-submissions/${sub.id}/approve`);
      toast.success("Payment approved · plan activated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const submitReject = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/payment-submissions/${reject.id}/reject`, { reason });
      toast.success("Payment rejected");
      setReject(null); setReason("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const fullUrl = (u) => u ? `${API.replace(/\/api$/, "")}${u}` : "";

  const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-6" data-testid="admin-verify-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white">Payment Verification</h1>
        <p className="text-slate-400 mt-1">Review shop subscription payments. Approve to activate plan + create commission.</p>
      </div>

      <div className="bg-[#151A23] border border-slate-800 rounded-md">
        <div className="p-4 border-b border-slate-800 flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {["pending", "approved", "rejected", "all"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`verify-filter-${f}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border capitalize ${filter === f ? "bg-amber-500 border-amber-500 text-slate-900" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                {f}{filter !== f && counts[f] ? <span className="ml-1.5 opacity-60">{counts[f]}</span> : ""}
              </button>
            ))}
          </div>
          <div className="text-sm text-slate-500 ml-auto">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Shop</th>
                <th className="text-left px-4 py-3 font-semibold">Plan</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-left px-4 py-3 font-semibold">Method</th>
                <th className="text-left px-4 py-3 font-semibold">Txn ID</th>
                <th className="text-left px-4 py-3 font-semibold">Proof</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">No submissions in this filter.</td></tr>}
              {rows.map((s) => (
                <tr key={s.id} className="border-t border-slate-800 hover:bg-slate-800/40" data-testid={`verify-row-${s.id}`}>
                  <td className="px-4 py-3 text-xs tabular text-slate-400">{(s.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{s.shop_name}</div>
                    <div className="text-xs text-slate-500 tabular">{s.shop_owner_email}</div>
                  </td>
                  <td className="px-4 py-3 capitalize text-slate-300">{s.plan_id}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold text-white">{fmtPKR(s.amount)}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{s.payment_method}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs tabular">{s.transaction_id || "—"}</td>
                  <td className="px-4 py-3">
                    {s.screenshot_url ? (
                      <button onClick={() => setPreview(s)} className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 text-xs font-semibold" data-testid={`verify-preview-${s.id}`}>
                        <FileImage className="w-4 h-4" /> View
                      </button>
                    ) : <span className="text-slate-600 text-xs">No file</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold border ${STATUS_TONE[s.status]}`}>
                      {s.status === "approved" && <CheckCircle2 className="w-3 h-3" />}
                      {s.status === "rejected" && <XCircle className="w-3 h-3" />}
                      {s.status === "pending" && <Hourglass className="w-3 h-3" />}
                      {s.status}
                    </span>
                    {s.status === "rejected" && s.reject_reason && (
                      <div className="text-[10px] text-red-300 mt-0.5">{s.reject_reason}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {s.status === "pending" && (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve(s)} data-testid={`verify-approve-${s.id}`}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" onClick={() => setReject(s)} data-testid={`verify-reject-${s.id}`}>
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Screenshot preview */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#151A23] text-slate-200 border border-slate-800" data-testid="verify-preview-dialog">
          <DialogHeader><DialogTitle className="text-white">Payment proof · {preview?.shop_name}</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><div className="text-xs text-slate-500 uppercase">Amount</div><div className="font-bold tabular">{fmtPKR(preview.amount)}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Method</div><div className="font-bold capitalize">{preview.payment_method}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Txn ID</div><div className="font-mono">{preview.transaction_id || "—"}</div></div>
                <div><div className="text-xs text-slate-500 uppercase">Notes</div><div className="text-xs">{preview.notes || "—"}</div></div>
              </div>
              {preview.screenshot_url && (
                <div className="rounded-md border border-slate-700 bg-slate-900 overflow-hidden flex items-center justify-center">
                  {/\.pdf$/i.test(preview.screenshot_url) ? (
                    <a href={fullUrl(preview.screenshot_url)} target="_blank" rel="noreferrer" className="p-8 text-sky-300 inline-flex items-center gap-2"><ExternalLink className="w-4 h-4" /> Open PDF in new tab</a>
                  ) : (
                    <img src={fullUrl(preview.screenshot_url)} alt="Payment proof" className="max-h-[60vh] object-contain" data-testid="verify-preview-img" />
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!reject} onOpenChange={(v) => !v && setReject(null)}>
        <DialogContent className="max-w-md bg-[#151A23] text-slate-200 border border-slate-800" data-testid="verify-reject-dialog">
          <DialogHeader><DialogTitle className="text-white">Reject payment · {reject?.shop_name}</DialogTitle></DialogHeader>
          <form onSubmit={submitReject} className="space-y-4">
            <div className="text-sm text-slate-400">Tell the shop why you're rejecting (visible to them).</div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Textarea required rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Amount mismatch / proof unclear / duplicate" className="bg-slate-900 border-slate-700 text-slate-200" data-testid="verify-reject-reason" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" data-testid="verify-reject-submit">Reject</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
